import React from "react";
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, User, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Convention {
  id: string;
  label: string;
  square_suffix: string;
  vertical_suffix: string;
  landscape_suffix: string | null;
  user_id: string | null;
}

interface ManageConventionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConventionDeleted: () => void;
}

export function ManageConventionsDialog({ open, onOpenChange, onConventionDeleted }: ManageConventionsDialogProps) {
  const [conventions, setConventions] = useState<Convention[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Convention | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchConventions();
    }
  }, [open]);

  const fetchConventions = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('naming_conventions')
      .select('*')
      .or(`user_id.is.null,user_id.eq.${user?.id}`)
      .order('user_id', { ascending: true, nullsFirst: true })
      .order('label');

    if (!error && data) {
      setConventions(data);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    const { error } = await supabase
      .from('naming_conventions')
      .delete()
      .eq('id', deleteTarget.id);

    setDeleting(false);
    setDeleteTarget(null);

    if (error) {
      toast({
        title: 'Error deleting convention',
        description: error.message,
        variant: 'destructive'
      });
    } else {
      toast({
        title: 'Convention deleted',
        description: 'The naming convention has been removed'
      });
      fetchConventions();
      onConventionDeleted();
    }
  };

  const customConventions = conventions.filter(c => c.user_id !== null);
  const systemConventions = conventions.filter(c => c.user_id === null);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Naming Conventions</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Custom Conventions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Your Custom Conventions</span>
                <Badge variant="secondary">{customConventions.length}</Badge>
              </div>

              {customConventions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No custom conventions yet. Add one when uploading creatives.
                </p>
              ) : (
                <div className="space-y-2">
                  {customConventions.map((conv) => (
                    <div
                      key={conv.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div className="space-y-1">
                        <p className="font-medium text-sm">{conv.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {conv.square_suffix} / {conv.vertical_suffix}
                          {conv.landscape_suffix && ` / ${conv.landscape_suffix}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteTarget(conv)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* System Conventions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">System Defaults</span>
                <Badge variant="outline">{systemConventions.length}</Badge>
              </div>

              <div className="space-y-2">
                {systemConventions.slice(0, 6).map((conv) => (
                  <div
                    key={conv.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-sm">{conv.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {conv.square_suffix} / {conv.vertical_suffix}
                        {conv.landscape_suffix && ` / ${conv.landscape_suffix}`}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">System</Badge>
                  </div>
                ))}
                {systemConventions.length > 6 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    + {systemConventions.length - 6} more system conventions
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Convention?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.label}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
